import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllLicStates } from '../api/States';
import { getAllProfessions, Profession } from '../api/Professions';
import { getCyclesForUser, Cycle } from '../api/Cycles';
import { LicState, UserLicense } from '../types';
import { getLicenseDeleteImpact, deleteLicenseWithPassword, LicenseDeleteImpact, updateUserProfile } from '../api/Users';
import api from '../utils/api';
import LicenseForm from '../components/LicenseForm';
import DeleteLicenseModal from '../components/DeleteLicenseModal';
import { useAuth } from '../useAuth';

const UserLicenses: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [states, setStates] = useState<LicState[]>([]);
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [userLicenses, setUserLicenses] = useState<UserLicense[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [classCount, setClassCount] = useState<number>(0);
  const [showNextSteps, setShowNextSteps] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [formInitial, setFormInitial] = useState<Partial<UserLicense>>({});
  const [userDOB, setUserDOB] = useState<string | undefined>(undefined);
  
  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState<LicenseDeleteImpact | null>(null);
  const [deletingLicense, setDeletingLicense] = useState(false);

  const openAddModal = () => {
    setFormMode('add');
    setFormInitial({ professionID: user?.professionID || '' }); // Pre-fill with user's profession
    setShowFormModal(true);
  };

  const openEditModal = (idx: number) => {
    setFormMode('edit');
    setEditIdx(idx);
    setFormInitial(userLicenses[idx]);
    setShowFormModal(true);
  };

  const handleFormSubmit = async (data: Partial<UserLicense> & { dateOfBirth?: string }) => {
    setError('');
    setSuccessMessage('');

    const { dateOfBirth, ...licenseData } = data;
    if (dateOfBirth && user?.id) {
      try {
        await updateUserProfile(user.id, { dateOfBirth });
        setUserDOB(dateOfBirth);
      } catch {
        setError('Failed to save date of birth — your renewal cycle may not auto-compute correctly.');
      }
    }

    if (formMode === 'add') {
      try {
        const res = await api.post('/api/users/me/licenses', licenseData);
        setUserLicenses(licenses => [...licenses, res.data]);
        setShowFormModal(false);
        
        // Refresh cycles data to check if user needs to add cycles
        const updatedCycles = await getCyclesForUser();
        setCycles(updatedCycles);
        
        // Check if user has classes
        const userRes = await api.get('/api/users/me');
        const userClassCount = userRes.data.cecRecords?.length || 0;
        setClassCount(userClassCount);
        
        // Show success message
        setSuccessMessage('✓ License added successfully!');
        
        // Show next steps if needed
        if (updatedCycles.length === 0 || userClassCount === 0) {
          setShowNextSteps(true);
        }
        
        setTimeout(() => {
          setSuccessMessage('');
          setShowNextSteps(false);
        }, 10000);
      } catch (err: any) {
        setError('Failed to add license.');
      }
    } else if (formMode === 'edit' && editIdx !== null) {
      try {
        const lic = userLicenses[editIdx];
        const res = await api.put(`/api/users/me/licenses/${lic._id}`, licenseData);
        setUserLicenses(licenses => licenses.map((l, i) => i === editIdx ? res.data : l));
        setShowFormModal(false);
        setSuccessMessage('License updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err: any) {
        setError('Failed to update license.');
      }
    }
  };

  const handleDelete = async () => {
    if (formMode === 'edit' && editIdx !== null) {
      try {
        const lic = userLicenses[editIdx];
        if (!lic._id) {
          setError('Invalid license ID');
          return;
        }
        
        setError('');
        setSuccessMessage('');
        
        // Fetch impact analysis
        const impact = await getLicenseDeleteImpact(lic._id);
        setDeleteImpact(impact);
        setShowDeleteModal(true);
      } catch (err: any) {
        const message = err.response?.data?.error || err.message || 'Failed to analyze license deletion impact';
        setError(message);
      }
    }
  };

  const handleConfirmDelete = async (password: string) => {
    if (editIdx === null) return;
    
    const lic = userLicenses[editIdx];
    if (!lic._id) {
      setError('Invalid license ID');
      return;
    }
    
    setDeletingLicense(true);
    
    try {
      const result = await deleteLicenseWithPassword(lic._id, password);
      
      // Update local state
      setUserLicenses(licenses => licenses.filter((_, i) => i !== editIdx));
      
      // Close modals
      setShowDeleteModal(false);
      setShowFormModal(false);
      setDeleteImpact(null);
      
      // Show success message with details
      setSuccessMessage(
        `License deleted successfully! Removed ${result.deletedCycles} cycle(s) and ${result.deletedAssignments} association(s).`
      );
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      // Error will be displayed in the modal
      throw err;
    } finally {
      setDeletingLicense(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteImpact(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statesRes, professionsRes, userRes, cyclesData] = await Promise.all([
          getAllLicStates(),
          getAllProfessions(),
          api.get('/api/users/me'),
          getCyclesForUser()
        ]);
        setStates(statesRes);
        setProfessions(professionsRes);
        setUserLicenses(userRes.data.licenses || []);
        setUserDOB(userRes.data.dateOfBirth);
        setCycles(cyclesData);
        setError('');
      } catch (err: any) {
        setError('Failed to load license data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get cycle count for a specific state
  const getCycleCountForState = (stateID: number): number => {
    return cycles.filter(cycle => {
      const cycleStateID = (cycle as any).stateid ?? cycle.stateID;
      return cycleStateID === stateID;
    }).length;
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 gap-3">
        <div>
          <h2 className="mb-1">My State Licenses</h2>
          <p className="text-muted mb-0 small">
            Manage which states you hold professional licenses in
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={openAddModal}>
          + Add New License
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
        </div>
      )}

      {/* Next Steps Guide */}
      {showNextSteps && (
        <div className="alert alert-info alert-dismissible fade show" role="alert">
          <h5 className="alert-heading">
            <i className="bi bi-info-circle me-2"></i>
            Next Steps to Start Tracking
          </h5>
          <hr />
          <div className="mb-2">
            {cycles.length === 0 && (
              <div className="mb-3">
                <strong>1. Add a Renewal Cycle</strong>
                <p className="mb-2 small">Create a tracking period for your license renewal (e.g., 2024-2026).</p>
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={() => navigate('/cycles')}
                >
                  <i className="bi bi-calendar-plus me-1"></i>
                  Go to Cycles
                </button>
              </div>
            )}
            {classCount === 0 && (
              <div>
                <strong>{cycles.length === 0 ? '2' : '1'}. Add Your CE Classes</strong>
                <p className="mb-2 small">Upload certificates and track your continuing education credits.</p>
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={() => navigate('/add-class')}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  Add Class
                </button>
              </div>
            )}
          </div>
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setShowNextSteps(false)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Stats Summary */}
      {userLicenses.length > 0 && (
        <div className="alert alert-info mb-4">
          <strong>Managing licenses in {userLicenses.length} state{userLicenses.length !== 1 ? 's' : ''}</strong>
        </div>
      )}

      {/* License Cards */}
      {userLicenses.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <h4 className="text-muted mb-3">No State Licenses Added Yet</h4>
            <p className="text-muted mb-4">
              Add the states where you hold professional licenses<br />
              to track your CEC compliance requirements
            </p>
            <button className="btn btn-primary" onClick={openAddModal}>
              + Add Your First License
            </button>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {userLicenses.map((lic, idx) => {
            const state = states.find(s => s.stateID === lic.stateID);
            const profession = professions.find(p => p.professionID === lic.professionID);
            const cycleCount = getCycleCountForState(lic.stateID);
            const stateName = state ? state.state : `State ID ${lic.stateID}`;
            const stateAbbr = state?.state || 'N/A';
            const professionName = profession ? profession.name : lic.professionID || 'N/A';
            
            return (
              <div className="col-12 col-md-6 col-xl-3" key={lic._id || idx}>
                <div className="card h-100" style={lic.planInactive ? { opacity: 0.85, background: '#f9fafb' } : undefined}>
                  <div className="card-body">
                    {/* State Header */}
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h3 className="mb-0 text-primary">{stateName}</h3>
                      </div>
                      {lic.planInactive ? (
                        <span className="badge bg-secondary">🔒 Locked</span>
                      ) : lic.status === 'active' ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </div>

                    {/* License Info */}
                    <div className="mb-3">
                      <div className="small text-muted mb-1">Profession</div>
                      <div className="fw-bold">{professionName}</div>
                    </div>

                    <div className="mb-3">
                      <div className="small text-muted mb-1">
                        {lic.professionID === 'LAW' ? 'Registration Number' : 'License Number'}
                      </div>
                      <div className="fw-bold">{lic.licenseNumber}</div>
                    </div>

                    {/* Cycle Info */}
                    {cycleCount > 0 && (
                      <div className="mb-3">
                        <div className="small text-muted">
                          {cycleCount} compliance cycle{cycleCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                    )}

                    {/* Plan-lock notice */}
                    {lic.planInactive && (
                      <div className="small text-muted mb-2" style={{ fontStyle: 'italic' }}>
                        Locked by your current plan — its compliance cycle is preserved,
                        not deleted. <a href="/pricing">Upgrade to restore →</a>
                      </div>
                    )}
                  </div>

                  {/* Card Footer */}
                  <div className="card-footer bg-white border-0">
                    <div className="d-flex flex-column gap-2">
                      <button 
                        className="btn btn-info btn-sm"
                        onClick={() => navigate(`/requirements/${lic.professionID}/${lic.stateID}`)}
                      >
                        📋 View CE Requirements
                      </button>
                      <button 
                        className="btn btn-outline-primary btn-sm" 
                        onClick={() => openEditModal(idx)}
                      >
                        Edit License
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* License Add/Edit Modal */}
      {showFormModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{formMode === 'edit' ? 'Edit License' : 'Add New License'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowFormModal(false)}></button>
              </div>
              <div className="modal-body">
                <LicenseForm
                  states={states}
                  professions={professions}
                  initialData={formInitial}
                  onSubmit={handleFormSubmit}
                  onCancel={() => setShowFormModal(false)}
                  mode={formMode}
                  onDelete={formMode === 'edit' ? handleDelete : undefined}
                  hideProfession={true}
                  userProfessionID={user?.professionID}
                  existingDateOfBirth={userDOB}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteImpact && editIdx !== null && (
        <DeleteLicenseModal
          impact={deleteImpact}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          loading={deletingLicense}
          stateName={states.find(s => s.stateID === userLicenses[editIdx].stateID)?.state || 'Unknown State'}
        />
      )}
    </div>
  );
};

export default UserLicenses;
